import {
  authenticationRequestInterceptor,
  authenticationResponseInterceptor,
} from '@/requests/interceptors/authentication.interceptor';
import { handleInterceptError } from '@/requests/interceptors/error.interceptor';
import { httpInterceptor } from '@/requests/interceptors/http.interceptor';
import {
  cancellationRequestInterceptor,
  cancellationResponseErrorInterceptor,
  cancellationResponseInterceptor,
} from '@/requests/interceptors/requestCancellation.interceptor';
import { requestOriginInterceptor } from '@/requests/interceptors/requestOrigin.interceptor';
import { conflictRetryInterceptor } from '@/requests/interceptors/retry.interceptor';
import { sessionIdInterceptor } from '@/requests/interceptors/sessionId.interceptor';
import { versionInterceptor } from '@/requests/interceptors/version.interceptor';
import {
  type SeeqAxiosRequestConfig,
  type SeeqAxiosResponse,
  type SeeqInternalAxiosRequestConfig,
  extractError,
  getAxiosInstance as getApiAxiosInstance,
  getPendingRequestsCount,
  isAxiosError,
} from '@seeqdev/api';
import type { AxiosInstance } from 'axios';

export function setupInterceptors(axiosInstance: AxiosInstance) {
  // The order of request interceptors matters, even for the error handling!
  // They are executed in REVERSED order: https://github.com/axios/axios/issues/1663#issue-339555912
  // So the last one needs to be the `cancellationRequestInterceptor` because it's the one that adds the requestId
  axiosInstance.interceptors.request.use(httpInterceptor, handleInterceptError);
  axiosInstance.interceptors.request.use(authenticationRequestInterceptor, handleInterceptError);
  axiosInstance.interceptors.request.use(requestOriginInterceptor, handleInterceptError);
  axiosInstance.interceptors.request.use(sessionIdInterceptor, handleInterceptError);
  axiosInstance.interceptors.request.use(cancellationRequestInterceptor, handleInterceptError);

  // response interceptor first callback is if status is <=200, second callback to handle errors
  axiosInstance.interceptors.response.use((response) => response, authenticationResponseInterceptor);
  axiosInstance.interceptors.response.use(versionInterceptor, handleInterceptError);
  axiosInstance.interceptors.response.use((response) => response, conflictRetryInterceptor);
  axiosInstance.interceptors.response.use((response) => response, handleInterceptError);
  axiosInstance.interceptors.response.use(cancellationResponseInterceptor, cancellationResponseErrorInterceptor);
}

/**
 * Exposes an instance of our properly configured axios-instance to the SDK
 * @deprecated use `getAxiosInstance` from `@seeqdev/api` instead
 */
export const getAxiosInstance = getApiAxiosInstance;

/**
 * Exposes the current count of pending requests, which is tracked by our interceptors
 * @deprecated use `getPendingRequestsCount` from `@seeqdev/api` instead
 */
export const getPendingRequestCount = getPendingRequestsCount;

export {
  /**
   * @deprecated use `extractError` from `@seeqdev/api` instead
   */
  extractError,
  /**
   * @deprecated use `isAxiosError` from `@seeqdev/api` instead
   */
  isAxiosError,
};
export type {
  /**
   * @deprecated use `SeeqAxiosRequestConfig` from `@seeqdev/api` instead
   */

  SeeqAxiosRequestConfig,
  /**
   * @deprecated use `SeeqInternalAxiosRequestConfig` from `@seeqdev/api` instead
   */
  SeeqInternalAxiosRequestConfig,
  /**
   * @deprecated use `SeeqAxiosResponse` from `@seeqdev/api` instead
   */
  SeeqAxiosResponse,
};
